import React from 'react';
import { formatSeconds, revWindow } from '@revdotcom/common';
import { Mp3Helper } from './Mp3Helper';
import { DownloadTrackerHelper } from './DownloadTrackerHelper';
import { UserMediaState, ToolState } from './OnlineVoiceRecorderModels';
import { ModernRecordingWaveform } from './ModernRecordingWaveform';
import { PreviewAudio } from './PreviewAudio';
import { SignUpGate } from './SignUpGate';
import { MediaRecorder } from './MediaRecorder';
/**
 * Online Voice Recorder component that allows the user to record, preview,
 * and download audio from their microphone on supported browsers.
 */
export class OnlineVoiceRecorder extends React.Component {
    constructor(props) {
        super(props);
        this.resetAndClearData = async () => {
            this.resetTimer();
            this.mp3BufferCollection = [];
            if (this.mediaRecorder) {
                await this.mediaRecorder.stop();
            }
        };
        this.processAudio = (data) => {
            if (this.state.toolState !== ToolState.Recording) {
                return;
            }
            const mp3Buffer = this.mp3Helper.encodeBuffer([data]);
            if (mp3Buffer.length > 0) {
                this.mp3BufferCollection.push(mp3Buffer);
            }
        };
        this.startTimer = () => {
            if (!this.timer) {
                this.timer = window.setInterval(() => {
                    if (this.state.toolState === ToolState.Recording) {
                        this.setState({
                            recordingTime: this.state.recordingTime + 1,
                        });
                    }
                }, 1000);
            }
        };
        this.resetTimer = () => {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
                this.setState({ recordingTime: 0 });
            }
        };
        this.getAudioContext = () => {
            return new window.AudioContext();
        };
        this.setupWarningBeforeUnload = () => {
            window.addEventListener('beforeunload', this.warnBeforeUnload);
        };
        this.warnBeforeUnload = async (event) => {
            if (this.state.toolState === ToolState.Recording) {
                await this.pauseRecording();
            }
            if (this.state.toolState !== ToolState.Ready) {
                event.preventDefault();
                event.returnValue = 'Changes you made may not be saved.';
            }
        };
        this.setupDisconnectOnUnload = () => {
            window.addEventListener('unload', this.resetAndClearData);
        };
        this.startRecording = async () => {
            var _a;
            window.analytics.track('Button Click', {
                altEventName: 'Online Voice Recorder: Record',
                stateChange: 'Redirectless page change',
                locationUrl: revWindow.baseUrl(),
                buttonText: 'Record',
                buttonLocation: 'Main Page',
            });
            try {
                this.mp3Helper = new Mp3Helper({
                    channelCount: 1,
                    sampleRate: (_a = this.mediaRecorder.sampleRate) !== null && _a !== void 0 ? _a : new AudioContext().sampleRate,
                    bitRate: 64,
                });
                const result = await this.mediaRecorder.start();
                if (!result.started) {
                    console.error('[OnlineVoiceRecorder] Failed to start recording:', result.error);
                    this.setState({
                        userMediaState: UserMediaState.Unavailable,
                    });
                    return;
                }
                this.setupWarningBeforeUnload();
                this.setupDisconnectOnUnload();
                this.setState({
                    toolState: ToolState.Recording,
                    userMediaState: UserMediaState.Available,
                }, this.startTimer);
            }
            catch (error) {
                console.error('[OnlineVoiceRecorder] Error starting recording:', error);
                console.error('[OnlineVoiceRecorder] Error stack:', error instanceof Error ? error.stack : 'No stack');
                this.setState({
                    userMediaState: UserMediaState.Unavailable,
                });
            }
        };
        this.pauseRecording = async () => {
            await this.mediaRecorder.pause();
            const finalMp3Buffer = this.mp3Helper.getFinalMp3Buffer();
            if (finalMp3Buffer.length > 0) {
                this.mp3BufferCollection.push(finalMp3Buffer);
            }
            const url = this.mp3Helper.generateDownloadableUrl(this.mp3BufferCollection);
            this.setState({
                toolState: ToolState.Paused,
                urlDownloadLink: url,
            });
        };
        this.resumeRecording = async () => {
            try {
                await this.mediaRecorder.resume();
                this.setState({
                    toolState: ToolState.Recording,
                });
            }
            catch (_a) {
                this.setState({
                    userMediaState: UserMediaState.Unavailable,
                });
            }
        };
        this.previewAudio = () => {
            this.setState({
                toolState: ToolState.Previewing,
            });
        };
        this.warnBeforeDeleting = async () => {
            await this.mediaRecorder.pause();
            this.setState({
                toolState: ToolState.Deleting,
            });
        };
        this.deleteRecording = async () => {
            await this.resetAndClearData();
            window.analytics.track('Button Click', {
                altEventName: 'Online Voice Recorder: Delete',
                stateChange: 'Redirectless page change',
                locationUrl: revWindow.baseUrl(),
                buttonText: 'Delete',
                buttonLocation: 'Main Page (after file recorded)',
            });
            this.setState({
                toolState: ToolState.Ready,
            });
        };
        this.trackAppStoreButtonClick = () => {
            window.analytics.track('Button Click', {
                altEventName: `Online Voice Recorder: RVR iOS App Store`,
                stateChange: 'Redirectless page change',
                locationUrl: revWindow.baseUrl(),
                buttonText: 'Download on the App Store',
                buttonLocation: 'Main Page',
            });
        };
        this.trackGooglePlayStoreButtonClick = () => {
            window.analytics.track('Button Click', {
                altEventName: `Online Voice Recorder: RVR Google Play Store`,
                stateChange: 'Redirectless page change',
                locationUrl: revWindow.baseUrl(),
                buttonText: 'Get it on Google Play',
                buttonLocation: 'Main Page',
            });
        };
        this.onDownloadClick = () => {
            DownloadTrackerHelper.handleDownloadClick(OnlineVoiceRecorder.DownloadTrackerKey, this.props.isAuthenticated);
        };
        this.returnToPreviousState = async () => {
            switch (this.state.previousToolState) {
                case ToolState.Paused:
                    await this.pauseRecording();
                    break;
                case ToolState.Previewing:
                    this.previewAudio();
                    break;
                case ToolState.Recording:
                    await this.resumeRecording();
                    break;
                case ToolState.Deleting:
                    await this.deleteRecording();
                    break;
                case ToolState.Ready:
                    this.setState({
                        toolState: ToolState.Ready,
                    });
                    break;
                default:
                    break;
            }
        };
        this.handleUploadComplete = () => {
            this.setState({
                toolState: ToolState.Uploaded,
            });
        };
        this.renderUnsupportedState = () => {
            return (React.createElement("div", { className: "card pv3 center" },
                React.createElement("div", { id: "recorder-unsupported-img", className: "bg-center" }),
                React.createElement("div", { className: "black ph4 fw5 f6" },
                    React.createElement("div", { className: "pv3" },
                        "oh no! ",
                        React.createElement("br", null),
                        "Looks like our voice recorder is not compatible with your browser."),
                    React.createElement("div", { className: "pv3" }, "Go get our voice recorder app or open this page in Safari to start recording now!")),
                React.createElement("div", { className: "mt3" },
                    React.createElement("a", { id: "voice-recorder-apple", className: "no-underline pointer ph1", href: "https://go.onelink.me/rFGj?pid=Rev%26c=TCLanding", target: "_blank", onClick: this.trackAppStoreButtonClick, rel: "noreferrer" },
                        React.createElement("img", { alt: "App Store Badge", src: "/content/curve/img/app-store-badge.svg" })),
                    React.createElement("a", { id: "voice-recorder-google", className: "no-underline pointer ph1", href: "https://play.google.com/store/apps/details?id=com.rev.revcorder", target: "_blank", onClick: this.trackGooglePlayStoreButtonClick, rel: "noreferrer" },
                        React.createElement("img", { alt: "Google Play Badge", src: "/content/curve/img/google-play-badge.svg" })))));
        };
        this.renderRecordingTimer = () => {
            const displayTime = formatSeconds(this.state.recordingTime, true);
            return (React.createElement("div", { className: "recording-timer" }, displayTime));
        };
        this.renderReadyState = () => {
            return (React.createElement("div", { className: "card ready-card" },
                React.createElement("div", { className: "ready-mic-icon" }),
                React.createElement("div", { className: "ready-text" }, "The mic is yours .... start recording"),
                React.createElement("button", { className: "ready-button", onClick: this.startRecording }, "Record")));
        };
        this.renderBlockedMicState = () => {
            return (React.createElement("div", { className: "card pv3 center" },
                React.createElement("div", { id: "recorder-unsupported-img", className: "mv1 bg-center" }),
                React.createElement("div", { className: "black fw5 f6 ph5-ns ph3" },
                    React.createElement("div", { className: "pv3" }, "It looks like we don\u2019t have access to your microphone."),
                    React.createElement("div", { className: "pv3" }, "Please allow access through your browser and refresh this page."))));
        };
        this.renderSignupState = () => {
            return SignUpGate({
                toolName: 'Online Voice Recorder',
                returnUrl: '/onlinevoicerecorder',
            });
        };
        this.renderToolState = () => {
            return (React.createElement("div", { className: "card recording-card" },
                React.createElement("div", { className: "recording-status" }, "RECORDING"),
                React.createElement(ModernRecordingWaveform, { mediaStream: this.mediaRecorder.mediaStream }),
                this.renderRecordingTimer(),
                React.createElement("div", { className: "flex items-center justify-center mv2" },
                    React.createElement("button", { id: "pause-button", type: "button", className: "pause-button", onClick: this.pauseRecording }, "Pause"),
                    React.createElement("div", { title: "Delete", id: "delete-from-recording-icon", className: "delete-icon ml2 pointer", onClick: this.warnBeforeDeleting }))));
        };
        this.renderDeletingState = () => {
            return (React.createElement("div", { className: "card deleting-card" },
                React.createElement("div", { className: "deleting-message" }, "Are you sure you want to delete?"),
                React.createElement("div", { className: "deleting-actions" },
                    React.createElement("button", { id: "delete-button", type: "button", className: "delete-confirm-button", onClick: this.deleteRecording }, "Delete"),
                    React.createElement("button", { id: "not-sure-button", type: "button", className: "cancel-delete-button", onClick: this.state.previousToolState === ToolState.Recording
                            ? this.pauseRecording
                            : this.returnToPreviousState }, "Not Sure"))));
        };
        this.renderPausedState = () => {
            return (React.createElement("div", { className: "card pv3 center paused-card" },
                React.createElement("div", { className: "paused-status" }, "PAUSED"),
                React.createElement(PreviewAudio, { urlDownloadLink: this.state.urlDownloadLink, fileNameWithoutExtension: this.props.outputFileName
                        ? this.props.outputFileName
                        : OnlineVoiceRecorder.DefaultOutputFileName, interactive: false, awsUploadInfo: this.props.awsUploadInfo, isAutomatedTranscription: this.props.isAutomatedTranscription, warnBeforeDeleting: this.warnBeforeDeleting, mp3Helper: this.mp3Helper, segmentAudioToolName: "Online Voice Recorder", downloadCallback: this.onDownloadClick, onUploadComplete: this.handleUploadComplete }),
                React.createElement("div", { className: "paused-timer" }, formatSeconds(this.state.recordingTime, true)),
                React.createElement("div", { className: "paused-actions" },
                    React.createElement("button", { id: "resume-button", type: "button", className: "resume-button", onClick: this.resumeRecording }, "Resume"),
                    React.createElement("button", { id: "preview-button", type: "button", className: "preview-button", onClick: this.previewAudio }, "Preview"),
                    React.createElement("div", { title: "Delete", id: "delete-from-pause-icon", className: "delete-icon pointer", onClick: this.warnBeforeDeleting }))));
        };
        this.renderPreviewState = () => {
            return (React.createElement("div", { className: "card pv3 center recording-card preview-card" },
                React.createElement(PreviewAudio, { urlDownloadLink: this.state.urlDownloadLink, fileNameWithoutExtension: this.props.outputFileName
                        ? this.props.outputFileName
                        : OnlineVoiceRecorder.DefaultOutputFileName, interactive: true, awsUploadInfo: this.props.awsUploadInfo, isAutomatedTranscription: this.props.isAutomatedTranscription, warnBeforeDeleting: this.warnBeforeDeleting, mp3Helper: this.mp3Helper, segmentAudioToolName: "Online Voice Recorder", downloadCallback: this.onDownloadClick, onUploadComplete: this.handleUploadComplete })));
        };
        this.openTcCheckoutInNewTab = () => {
            const cartRedirectUrl = this.props.isAutomatedTranscription
                ? '/checkout/automated-transcription-files'
                : '/checkout/transcriptionfiles';
            revWindow.open(cartRedirectUrl);
        };
        this.renderUploadedState = () => {
            return (React.createElement("div", { className: "card pv3 center recording-card" },
                React.createElement("div", { className: "white pv3 fw6 f4" }, "Your file has been uploaded!"),
                React.createElement("div", { className: "flex flex-row items-center justify-center gap-3 mv4" },
                    React.createElement("a", { title: "Download", id: "download-icon-uploaded", className: "download-icon pointer", onClick: async () => {
                            const url = this.state.urlDownloadLink;
                            const downloadBtn = document.createElement('a');
                            downloadBtn.href = url;
                            downloadBtn.download = `${this.props.outputFileName
                                ? this.props.outputFileName
                                : OnlineVoiceRecorder.DefaultOutputFileName}.mp3`;
                            downloadBtn.click();
                            this.onDownloadClick();
                        } }),
                    React.createElement("button", { id: "go-to-cart-button", className: "transcribe-button", style: { maxWidth: '50%' }, onClick: this.openTcCheckoutInNewTab }, "GO TO CART"),
                    React.createElement("div", { title: "Start Over", id: "delete-icon-uploaded", className: "delete-icon pointer", onClick: this.deleteRecording }))));
        };
        this.renderVoiceRecorderTool = () => {
            const showSignUpGate = !this.props.isAuthenticated &&
                DownloadTrackerHelper.getDownloadCount(OnlineVoiceRecorder.DownloadTrackerKey) >=
                    OnlineVoiceRecorder.UnauthenticatedDownloadLimit;
            return (React.createElement("div", null,
                this.state.toolState === ToolState.Ready && showSignUpGate && this.renderSignupState(),
                this.state.toolState === ToolState.Ready &&
                    !showSignUpGate &&
                    this.state.userMediaState !== UserMediaState.Unavailable &&
                    this.renderReadyState(),
                this.state.toolState === ToolState.Ready &&
                    !showSignUpGate &&
                    this.state.userMediaState === UserMediaState.Unavailable &&
                    this.renderBlockedMicState(),
                this.state.toolState === ToolState.Recording && this.renderToolState(),
                this.state.toolState === ToolState.Paused && this.renderPausedState(),
                this.state.toolState === ToolState.Deleting && this.renderDeletingState(),
                this.state.toolState === ToolState.Previewing && this.renderPreviewState(),
                this.state.toolState === ToolState.Uploaded && this.renderUploadedState()));
        };
        this.state = {
            userMediaState: UserMediaState.Pending,
            toolState: ToolState.Ready,
            previousToolState: ToolState.Ready,
            recordingTime: 0,
            urlDownloadLink: '',
        };
        this.isRecorderSupported = navigator.mediaDevices ? true : false;
        this.mp3BufferCollection = [];
        try {
            this.mediaRecorder = new MediaRecorder({
                audio: { microphone: true },
                onData: this.processAudio,
            });
        }
        catch (error) {
            console.error('[OnlineVoiceRecorder] Failed to create MediaRecorder:', error);
            throw error;
        }
    }
    componentDidUpdate(_, prevState) {
        if (prevState.toolState !== prevState.previousToolState && prevState.toolState !== this.state.toolState) {
            this.setState({
                previousToolState: prevState.toolState,
            });
        }
    }
    async componentWillUnmount() {
        if (this.isRecorderSupported) {
            await this.resetAndClearData();
            window.removeEventListener('unload', this.resetAndClearData);
        }
    }
    render() {
        return (React.createElement("div", { id: "online-voice-recorder-container" }, this.isRecorderSupported ? this.renderVoiceRecorderTool() : this.renderUnsupportedState()));
    }
}
OnlineVoiceRecorder.DefaultOutputFileName = 'Rev';
OnlineVoiceRecorder.DownloadTrackerKey = 'RevOvrUnauthenticatedDownloadCount';
// Number of downloads an unauthenticated user gets before the sign-up gate appears.
OnlineVoiceRecorder.UnauthenticatedDownloadLimit = 20;
